// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * Brugere (admin) as data: what each account change sends, the checks before
 * it is sent, what an administrator may not do to their own account, and the
 * Danish line for a refusal. Pure, so it is testable without a DOM.
 *
 * Accounts are Supabase's: ruxd writes them through GoTrue's admin API
 * (`PATCH /users/{uid}`, `POST /users/{uid}/password-reset`). `disabled` is
 * Supabase's ban.
 */

import type { UserPatch } from '../api/auth';
import { ApiRequestError } from '../api/client';
import type { AuthUser, GlobalRole } from '../api/types';
import { GLOBAL_ROLE_LABELS, normalizeMemberEmail } from '../app/auth';

export type Check<T> = { ok: true; value: T } | { ok: false; reason: string };

/** `user_metadata.username`: ruxd allows at most 64 bytes, no control characters. */
export const MAX_USERNAME_BYTES = 64;

/** How long the search box waits after the last keystroke before asking the server. */
export const SEARCH_DEBOUNCE_MS = 250;

/** The inline editor's two fields. */
export interface UserDraft {
  username: string;
  email: string;
}

export function draftFor(user: AuthUser): UserDraft {
  return { username: user.display_name, email: user.email };
}

// eslint-disable-next-line no-control-regex
const CONTROL = /[\u0000-\u001f\u007f]/;

function utf8Length(text: string): number {
  return new TextEncoder().encode(text).length;
}

/**
 * The patch for only what changed. An empty username clears it (`null`), so
 * the e-mail shows instead. Nothing that is unchanged is sent.
 */
export function buildUserPatch(user: AuthUser, next: Partial<UserPatch>): UserPatch {
  const patch: UserPatch = {};
  if (next.role !== undefined && next.role !== user.role) patch.role = next.role;
  if (next.disabled !== undefined && next.disabled !== user.disabled) patch.disabled = next.disabled;
  if (next.email !== undefined) {
    const email = next.email.trim().toLowerCase();
    if (email !== user.email.trim().toLowerCase()) patch.email = email;
  }
  if (next.username !== undefined) {
    const username = (next.username ?? '').trim();
    if (username !== user.display_name.trim()) patch.username = username === '' ? null : username;
  }
  return patch;
}

/** Checks the inline editor and builds its patch; refuses an invalid or unchanged draft. */
export function userEditCheck(draft: UserDraft, user: AuthUser): Check<UserPatch> {
  const username = draft.username.trim();
  // Only a changed name is checked: an existing over-long name must not block an e-mail fix.
  if (username !== user.display_name.trim()) {
    if (CONTROL.test(username)) return { ok: false, reason: 'Navnet må ikke indeholde kontroltegn.' };
    if (utf8Length(username) > MAX_USERNAME_BYTES)
      return { ok: false, reason: `Navnet er for langt (højst ${MAX_USERNAME_BYTES} bytes).` };
  }
  const email = normalizeMemberEmail(draft.email);
  if (email === null) return { ok: false, reason: 'Skriv en gyldig e-mail.' };
  const patch = buildUserPatch(user, { username, email });
  if (Object.keys(patch).length === 0) return { ok: false, reason: 'Intet er ændret.' };
  return { ok: true, value: patch };
}

export function isSelf(user: AuthUser, myId: string | undefined): boolean {
  return myId !== undefined && user.id === myId;
}

/** An administrator cannot change their own role (they could only demote themselves; the server 409s). */
export function canChangeRole(user: AuthUser, myId: string | undefined): boolean {
  return !isSelf(user, myId);
}

/** An administrator cannot disable themselves; re-opening is never refused. */
export function canToggleDisabled(user: AuthUser, myId: string | undefined): boolean {
  return user.disabled || !isSelf(user, myId);
}

/** Active before disabled, then by name (or e-mail when there is none), Danish collation. */
export function sortUsers(users: readonly AuthUser[]): AuthUser[] {
  const key = (u: AuthUser) => (u.display_name || u.email).toLocaleLowerCase('da');
  return [...users].sort(
    (a, b) => Number(a.disabled) - Number(b.disabled) || key(a).localeCompare(key(b), 'da') || a.email.localeCompare(b.email),
  );
}

/** How many accounts have each role, active ones only. */
export function roleCounts(users: readonly AuthUser[]): Record<GlobalRole, number> {
  const counts: Record<GlobalRole, number> = { user: 0, group_manager: 0, admin: 0 };
  for (const u of users) if (!u.disabled) counts[u.role] += 1;
  return counts;
}

/** The name a row and its confirm buttons use. */
export function userName(user: AuthUser): string {
  return user.display_name || user.email;
}

/** What an account change was, for its error line. */
export type UserAction = 'load' | 'edit' | 'role' | 'disable' | 'enable' | 'reset';

const FALLBACK: Record<UserAction, string> = {
  load: 'Brugerne kunne ikke hentes.',
  edit: 'Ændringen kunne ikke gemmes.',
  role: 'Rollen kunne ikke ændres.',
  disable: 'Kontoen kunne ikke spærres.',
  enable: 'Kontoen kunne ikke genåbnes.',
  reset: 'Mailen kunne ikke sendes.',
};

/** The Danish line under a refused account change (or a failed list). */
export function userErrorText(cause: unknown, action: UserAction): string {
  if (cause instanceof ApiRequestError) {
    const message = cause.message;
    switch (cause.status) {
      case 400:
        if (/username/i.test(message)) return 'Navnet er ugyldigt (for langt eller med kontroltegn).';
        if (/email/i.test(message)) return 'Skriv en gyldig e-mail.';
        return 'Serveren forstod ikke ændringen.';
      case 403:
        return 'Kun administratorer kan se og ændre brugere.';
      case 404:
        return 'Brugeren findes ikke længere.';
      case 409:
        if (/local mode/i.test(message)) return 'Serveren kører i lokal tilstand og har ingen brugere.';
        if (/email/i.test(message)) return 'Der findes allerede en bruger med den e-mail.';
        if (/disable/i.test(message)) return 'Du kan ikke spærre din egen konto.';
        if (/administrator|admin/i.test(message))
          return 'Du kan ikke fjerne din egen administratorrolle. Bed en anden administrator om det.';
        return FALLBACK[action];
      case 422:
        return 'Supabase afviste ændringen som ugyldig.';
      case 502:
        return action === 'reset'
          ? 'Supabase afviste at sende mailen. Tjek serverens Supabase-opsætning.'
          : 'Supabase afviste ændringen. Tjek serverens Supabase-opsætning.';
      case 503:
        if (action === 'load') return 'Brugerdatabasen svarer ikke lige nu. Prøv igen om lidt.';
        if (/unreachable/i.test(message)) return 'Supabase svarer ikke lige nu. Prøv igen om lidt.';
        return 'Brugerkonti kan ikke ændres: serveren er ikke sat op til Supabase-administration.';
    }
  }
  return FALLBACK[action];
}

/** The line after a password-reset mail went out. */
export function resetSentText(user: AuthUser): string {
  return `Mail til nulstilling af adgangskode er sendt til ${user.email}.`;
}

/** The line after an account change succeeded; `user` is the account as it now is. */
export function changedText(user: AuthUser, action: Exclude<UserAction, 'load' | 'reset'>): string {
  const name = userName(user);
  switch (action) {
    case 'edit':
      return `${name} er gemt.`;
    case 'role':
      return `${name} er nu ${GLOBAL_ROLE_LABELS[user.role].toLowerCase()}.`;
    case 'disable':
      return `${name} er spærret og kan ikke logge ind.`;
    case 'enable':
      return `${name} er genåbnet.`;
  }
}
